import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  BarChart3,
  Bot,
  CheckCircle2,
  Globe2,
  Headphones,
  MessageSquareText,
  Send,
  ShieldCheck,
  Sparkles,
  UserRoundPlus
} from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";
type Message = { role: "user" | "assistant"; content: string; meta?: string };

const copy = {
  zh: {
    brand: "启序智服",
    nav: ["能力", "演示", "运营台"],
    lang: "EN",
    heroLabel: "独立案例站点 01 / AI 智能客服",
    title: "能接入企业业务的 AI 智能客服网站。",
    subtitle:
      "覆盖官网咨询、企业微信、飞书、公众号和小程序场景，用可控知识库回答问题，无法确认时自动转人工留资。",
    primary: "试用在线客服",
    secondary: "查看运营指标",
    trusted: "适用于 SaaS、电商、本地生活、教育与售后团队",
    metrics: [
      ["72%", "自动解决率"],
      ["1.8s", "首次响应"],
      ["24/7", "在线服务"]
    ],
    capabilitiesTitle: "不是聊天框演示，而是一套客服交付系统。",
    capabilities: [
      ["多渠道接入", "官网、企业微信、飞书、公众号、小程序消息统一进入工作台。"],
      ["受控知识库", "只基于 FAQ、产品文档、售后政策回答，降低胡编风险。"],
      ["转人工留资", "遇到高风险、低置信度或销售问题时，自动生成工单并收集线索。"],
      ["运营分析", "沉淀高频问题、满意度、转人工原因和知识库缺口。"]
    ],
    demoTitle: "在线客服 Demo",
    demoSubtitle: "问题会发到服务器，由 OpenClaw 调用 MiniMax 生成受控回复。",
    inputPlaceholder: "例如：你们支持企业微信和飞书接入吗？如果回答不了会怎么办？",
    send: "发送",
    sample: "填入示例",
    empty: "先输入一个客户问题。",
    leadTitle: "转人工留资",
    leadSubtitle: "模拟真实客服场景：当客户需要报价、集成或私有化部署时创建工单。",
    name: "姓名",
    contact: "联系方式",
    need: "需求描述",
    submitLead: "创建工单",
    dashboardTitle: "客服运营台",
    dashboardSubtitle: "销售演示时可直接讲清楚上线后如何运营和优化。",
    panels: [
      ["高频问题", "企业微信接入、私有化部署、报价、售后政策"],
      ["知识库缺口", "合同 SLA、专属行业话术、旧系统字段映射"],
      ["人工介入", "报价咨询、投诉处理、定制开发、权限审批"]
    ],
    footer: "启序科技 AI 案例站点 · 第 1 个真实独立网站"
  },
  en: {
    brand: "Qixu Support AI",
    nav: ["Features", "Demo", "Ops"],
    lang: "中文",
    heroLabel: "Standalone Case Site 01 / AI Customer Service",
    title: "An AI support site ready for real business workflows.",
    subtitle:
      "Handle web chat, WeCom, Feishu, official accounts and mini programs with grounded answers, handoff capture and service analytics.",
    primary: "Try the chatbot",
    secondary: "View ops board",
    trusted: "Built for SaaS, commerce, local service, education and after-sales teams",
    metrics: [
      ["72%", "auto resolution"],
      ["1.8s", "first response"],
      ["24/7", "coverage"]
    ],
    capabilitiesTitle: "More than a chat widget: a complete support workflow.",
    capabilities: [
      ["Omnichannel intake", "Route website, WeCom, Feishu and mini-program messages into one support surface."],
      ["Grounded knowledge", "Answer only from FAQ, product docs and policy content to reduce hallucination risk."],
      ["Human handoff", "Create a ticket and collect lead details for pricing, integration or sensitive questions."],
      ["Operations analytics", "Track top questions, satisfaction, handoff reasons and knowledge gaps."]
    ],
    demoTitle: "Live Support Demo",
    demoSubtitle: "Messages are sent to the server, where OpenClaw calls MiniMax for grounded replies.",
    inputPlaceholder: "Example: Do you support WeCom and Feishu integration? What happens if the bot is unsure?",
    send: "Send",
    sample: "Use sample",
    empty: "Please enter a customer question first.",
    leadTitle: "Human Handoff",
    leadSubtitle: "Simulate real support: create a ticket when users need pricing, integration or private deployment.",
    name: "Name",
    contact: "Contact",
    need: "Need",
    submitLead: "Create ticket",
    dashboardTitle: "Support Ops Board",
    dashboardSubtitle: "A practical view for explaining post-launch operations and iteration.",
    panels: [
      ["Top questions", "WeCom integration, private deployment, pricing, after-sales policy"],
      ["Knowledge gaps", "Contract SLA, industry scripts, legacy field mapping"],
      ["Human handoff", "Pricing, complaints, custom development, permission approval"]
    ],
    footer: "Qixu Technology AI case site · first real standalone website"
  }
};

const sampleQuestions = {
  zh: "你们支持企业微信和飞书接入吗？如果机器人不知道答案应该怎么办？",
  en: "Do you support WeCom and Feishu integration? What should the bot do when it is not sure?"
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [input, setInput] = useState(sampleQuestions.zh);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        "您好，我是启序智服 AI 客服。您可以问我渠道接入、知识库、私有部署、转人工和运营指标相关问题。",
      meta: "ready"
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [leadStatus, setLeadStatus] = useState("");

  const stats = useMemo(
    () => [
      { label: "今日会话", value: "1,284", trend: "+18%" },
      { label: "自动解决", value: "924", trend: "72%" },
      { label: "待跟进工单", value: "36", trend: "-9%" }
    ],
    []
  );

  async function sendMessage() {
    const message = input.trim();
    if (!message || loading) return;
    setMessages((items) => [...items, { role: "user", content: message }]);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, locale })
      });
      const data = await response.json();
      setMessages((items) => [
        ...items,
        {
          role: "assistant",
          content: data.text || "当前客服暂时不可用，请转人工处理。",
          meta: `${data.provider || "unknown"} / ${data.model || "unknown"}`
        }
      ]);
    } catch {
      setMessages((items) => [
        ...items,
        { role: "assistant", content: "网络异常，已建议转人工处理。", meta: "network fallback" }
      ]);
    } finally {
      setLoading(false);
    }
  }

  async function submitLead(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      name: form.get("name"),
      contact: form.get("contact"),
      need: form.get("need")
    };
    const response = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    setLeadStatus(data.ok ? `工单已创建：${data.ticketId}` : "请完整填写留资信息");
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top" aria-label={t.brand}>
          <span>启</span>
          <strong>{t.brand}</strong>
        </a>
        <nav>
          <a href="#features">{t.nav[0]}</a>
          <a href="#demo">{t.nav[1]}</a>
          <a href="#ops">{t.nav[2]}</a>
        </nav>
        <button className="lang" onClick={() => setLocale(locale === "zh" ? "en" : "zh")}>
          <Globe2 size={16} />
          {t.lang}
        </button>
      </header>

      <section className="hero" id="top">
        <div className="hero-copy">
          <p className="eyebrow"><Sparkles size={16} />{t.heroLabel}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#demo">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#ops">{t.secondary}</a>
          </div>
          <p className="trusted">{t.trusted}</p>
        </div>
        <div className="hero-panel">
          <div className="panel-head">
            <Bot size={22} />
            <span>Live support desk</span>
          </div>
          <div className="chat-preview">
            <div className="bubble bot">您好，请问需要了解接入渠道、报价还是售后政策？</div>
            <div className="bubble user">我们想接入企业微信和官网。</div>
            <div className="bubble bot">可以接入。我会先确认渠道、知识库范围和是否需要转人工。</div>
          </div>
          <div className="metric-row">
            {t.metrics.map(([value, label]) => (
              <div key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <h2>{t.capabilitiesTitle}</h2>
        <div className="feature-grid">
          {[MessageSquareText, ShieldCheck, UserRoundPlus, BarChart3].map((Icon, index) => (
            <article key={t.capabilities[index][0]}>
              <Icon size={24} />
              <h3>{t.capabilities[index][0]}</h3>
              <p>{t.capabilities[index][1]}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="workspace" id="demo">
        <div className="chat-card">
          <div className="section-title">
            <Headphones size={22} />
            <div>
              <h2>{t.demoTitle}</h2>
              <p>{t.demoSubtitle}</p>
            </div>
          </div>
          <div className="messages" aria-live="polite">
            {messages.map((item, index) => (
              <div className={`message ${item.role}`} key={`${item.role}-${index}`}>
                <p>{item.content}</p>
                {item.meta && <span>{item.meta}</span>}
              </div>
            ))}
            {loading && <div className="message assistant"><p>正在生成受控回复...</p></div>}
          </div>
          <div className="composer">
            <textarea
              value={input}
              placeholder={t.inputPlaceholder}
              onChange={(event) => setInput(event.target.value)}
            />
            <div className="composer-actions">
              <button onClick={() => setInput(sampleQuestions[locale])}>{t.sample}</button>
              <button className="send" onClick={sendMessage} disabled={loading || !input.trim()}>
                {t.send}<Send size={16} />
              </button>
            </div>
          </div>
        </div>

        <form className="lead-card" onSubmit={submitLead}>
          <h2>{t.leadTitle}</h2>
          <p>{t.leadSubtitle}</p>
          <label>
            {t.name}
            <input name="name" defaultValue="张经理" />
          </label>
          <label>
            {t.contact}
            <input name="contact" defaultValue="demo@qixu.ai" />
          </label>
          <label>
            {t.need}
            <textarea name="need" defaultValue="希望接入企业微信、官网和现有 CRM，并了解私有化部署。" />
          </label>
          <button className="button primary" type="submit">
            {t.submitLead}<CheckCircle2 size={18} />
          </button>
          {leadStatus && <div className="lead-status">{leadStatus}</div>}
        </form>
      </section>

      <section className="ops" id="ops">
        <div>
          <h2>{t.dashboardTitle}</h2>
          <p>{t.dashboardSubtitle}</p>
        </div>
        <div className="stats">
          {stats.map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <em>{item.trend}</em>
            </div>
          ))}
        </div>
        <div className="ops-grid">
          {t.panels.map(([title, content]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{content}</p>
            </article>
          ))}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
